import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@f2m3/shared-frontend-ui';
import type { ChatMessage } from '../../api/types';
import ReminderCard from '../reminders/ReminderCard';

interface Props {
  messages: ChatMessage[];
  /** Omitted in read-only mode. */
  onSend?: (body: string) => void;
  sending?: boolean;
  /** Reminder bell: no composer — a staff thread only ever receives. */
  readOnly?: boolean;
  title?: string;
  emptyLabel?: string;
  /** The thread failed to load. Takes over the messages area — a failed
   * fetch cannot support the claim "you have no messages", so it must never
   * fall through to the empty state (docs/FRONTEND-GUIDELINES.md "Loading &
   * Error States"). */
  error?: boolean;
}

const ChatPanel = ({ messages, onSend, sending = false, readOnly = false, title, emptyLabel, error = false }: Props) => {
  const { t, i18n } = useTranslation();
  const [ text, setText ] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending || !onSend) return;
    onSend(body);
    setText('');
  };

  return (
    <div className="chat-panel">
      <div className="chat-header">{title ?? t('chat.title')}</div>
      <div className="chat-messages">
        {error ? (
          <div className="notification is-danger is-light">{t('chat.load_error')}</div>
        ) : (
          <>
            {messages.length === 0 && <div className="chat-empty">{emptyLabel ?? t('chat.empty')}</div>}
            {messages.map((m) => (m.kind === 'reminder' ? (
              <div key={m.id} className="chat-msg is-system">
                <ReminderCard message={m} />
                <span className="chat-msg__meta">{formatDateTime(m.created_at, i18n.language)}</span>
              </div>
            ) : (
              <div key={m.id} className={`chat-msg ${m.sender === 'customer' ? 'is-customer' : 'is-admin'}`}>
                {m.body}
                <span className="chat-msg__meta">
                  {m.sender === 'admin' && (m.admin_name ?? t('chat.from_shop')) + ' · '}
                  {formatDateTime(m.created_at, i18n.language)}
                </span>
              </div>
            )))}
          </>
        )}
        <div ref={endRef} />
      </div>
      {!readOnly && (
        <form className="chat-form" onSubmit={submit}>
          <input
            className="input"
            type="text"
            value={text}
            placeholder={t('chat.placeholder')}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="button is-primary" disabled={sending || !text.trim()}>
            {t('chat.send')}
          </button>
        </form>
      )}
    </div>
  );
};

export default ChatPanel;
